import React, { useEffect, useRef, useState } from "react";
import { Ticket, Check, Loader2, Sparkles, Calendar, Wallet } from "lucide-react";
import api, { formatRp } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * VoucherPicker — daftar voucher tersedia + tombol "Gunakan".
 * Props:
 *  - subtotal: number — untuk hitung discount_preview & eligibility
 *  - selected: voucher object | null (currently applied)
 *  - onPick: (voucher | null) => void — dipanggil saat user klik Gunakan/Lepas
 *  - autoApply: bool — auto-apply voucher TERBAIK saat mount (default true)
 */

function formatDateID(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return null; }
}

export default function VoucherPicker({ subtotal, selected, onPick, autoApply = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bestId, setBestId] = useState(null);

  useEffect(() => {
    setLoading(true);
    // Fetch keduanya bersamaan: list voucher + voucher TERBAIK (auto-apply candidate)
    Promise.all([
      api.get(`/vouchers/available?subtotal=${subtotal}`).then((r) => r.data || []).catch(() => []),
      api.get(`/vouchers/best?subtotal=${subtotal}`).then((r) => r.data?.voucher || null).catch(() => null),
    ]).then(([list, best]) => {
      setItems(list);
      // Prioritas badge: voucher dari /vouchers/best (kalau ada) → fallback ke
      // voucher eligible dengan discount preview tertinggi
      if (best && best.id) {
        setBestId(best.id);
      } else {
        const eligibleSorted = list
          .filter((v) => v.eligible)
          .sort((a, b) => (b.discount_preview || 0) - (a.discount_preview || 0));
        setBestId(eligibleSorted[0]?.id || null);
      }
    }).finally(() => setLoading(false));
  }, [subtotal]);

  // Auto-apply best voucher SEKALI saja saat mount/awal — pakai ref untuk
  // mencegah re-apply otomatis kalau user kemudian me-clear voucher secara manual.
  // Pendekatan ini menjaga semua dep di array (exhaustive-deps PASS) sambil
  // tetap "fire once" secara semantik.
  const hasAutoAppliedRef = useRef(false);
  useEffect(() => {
    if (!autoApply || selected || hasAutoAppliedRef.current) return;
    let cancelled = false;
    api.get(`/vouchers/best?subtotal=${subtotal}`)
      .then((r) => {
        if (cancelled) return;
        if (r.data?.voucher) {
          hasAutoAppliedRef.current = true;
          onPick(r.data.voucher);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [autoApply, selected, subtotal, onPick]);

  if (loading) {
    return <div className="text-xs text-[#5C5C5C] flex items-center gap-1" data-testid="voucher-picker-loading"><Loader2 size={12} className="animate-spin" /> Memuat voucher...</div>;
  }
  if (items.length === 0) {
    return <div className="text-xs text-[#5C5C5C]" data-testid="voucher-picker-empty">Belum ada voucher tersedia.</div>;
  }

  return (
    <div className="space-y-2" data-testid="voucher-picker">
      {items.map((v) => {
        const isSelected = selected && selected.id === v.id;
        const isBest = bestId === v.id && v.eligible;
        const reason = v.eligible ? null : v.reason_ineligible;
        const validUntil = formatDateID(v.valid_until);
        const validFrom = formatDateID(v.valid_from);
        const savings = v.type === "free_shipping"
          ? "Gratis Ongkir"
          : `Hemat ${formatRp(v.discount_preview)}`;
        return (
          <div
            key={v.id}
            className={`relative p-3 rounded-xl border ${isSelected ? "border-[#111827] system-soft-bg" : "border-[#E5E5E5]"} ${!v.eligible ? "opacity-60" : ""}`}
            data-testid={`voucher-${v.code}`}
          >
            {isBest && (
              <span
                className="absolute -top-2 left-3 px-2 py-0.5 rounded-md text-[10px] font-black tracking-wider uppercase text-white flex items-center gap-1 shadow"
                style={{ background: "linear-gradient(90deg, var(--brand-primary), color-mix(in srgb, var(--brand-primary) 70%, black))" }}
                data-testid={`voucher-best-badge-${v.code}`}
              >
                <Sparkles size={10} /> Voucher Terbaik
              </span>
            )}
            <div className="flex items-start gap-3">
              <Ticket className="brand-text-primary mt-0.5 shrink-0" size={18} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold flex items-center gap-2 flex-wrap">
                  {v.name || v.code}
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F0E5C0] text-[#7A5440]">{v.code}</span>
                </div>
                {v.eligible && (
                  <div className="text-xs font-bold text-[#2D6A4F] mt-0.5" data-testid={`voucher-savings-${v.code}`}>
                    {v.type === "free_shipping" ? "✓ Gratis Ongkir" : `Anda hemat ${formatRp(v.discount_preview)}`}
                  </div>
                )}
                <div className="text-[11px] text-[#5C5C5C] mt-1 space-y-0.5">
                  {!v.eligible && <div>{savings}</div>}
                  {v.min_purchase > 0 && (
                    <div className="flex items-center gap-1">
                      <Wallet size={10} /> Minimal belanja {formatRp(v.min_purchase)}
                    </div>
                  )}
                  {(validFrom || validUntil) && (
                    <div className="flex items-center gap-1">
                      <Calendar size={10} />
                      {validFrom && validUntil
                        ? `Berlaku ${validFrom} — ${validUntil}`
                        : validUntil
                          ? `Berlaku s/d ${validUntil}`
                          : `Mulai ${validFrom}`}
                    </div>
                  )}
                  {v.max_discount && v.type === "percentage" && (
                    <div>Maks diskon {formatRp(v.max_discount)}</div>
                  )}
                </div>
                {reason && <div className="text-[10px] text-amber-700 mt-1">{reason}</div>}
              </div>
              <Button
                size="sm"
                variant={isSelected ? "outline" : "default"}
                onClick={() => onPick(isSelected ? null : v)}
                disabled={!v.eligible && !isSelected}
                className={isSelected ? "rounded-lg" : "rounded-lg action-btn-primary"}
                data-testid={`voucher-pick-${v.code}`}
              >
                {isSelected ? <><Check size={14} className="mr-1" /> Dipakai</> : "Gunakan"}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
